/* Progressive enhancement: links, native details and Shopify forms work without JavaScript. */
(() => {
  if (window.AuluoHome) return;
  const instances = new WeakSet();
  const disclosures = new WeakMap();
  const menus = new WeakMap();
  const active = new Set();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motion = {duration: 360, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'};
  let observer;
  let scrollFrame = 0;

  function revealAll() {
    document.querySelectorAll('.auluo-reveal-ready').forEach(el => el.classList.add('auluo-revealed'));
    observer?.disconnect();
  }
  function closeMenu(menu, returnFocus = false) {
    if (!menu.open) return;
    if (menus.has(menu)) menus.get(menu).setOpen(false);
    else menu.open = false;
    if (returnFocus) menu.querySelector('summary')?.focus();
  }
  function enhanceMenu(menu) {
    const summary = menu.querySelector('summary');
    const panel = menu.querySelector('.auluo-mobile-nav');
    if (!summary || !panel) return;
    let desired = menu.open, animation;
    function settle() {
      if (animation) { animation.onfinish = null; animation.cancel(); animation = null; }
      menu.open = desired;
      menu.removeAttribute('data-menu-state');
    }
    function setOpen(value, animate = true) {
      if (value === desired && !animation) return;
      const start = menu.open ? getComputedStyle(panel) : null;
      const opacity = start?.opacity || '0';
      const transform = start?.transform || 'translateY(-10px)';
      desired = value; settle();
      summary.setAttribute('aria-expanded', String(value));
      if (!animate || reduced.matches || typeof panel.animate !== 'function') return;
      menu.open = true;
      menu.dataset.menuState = value ? 'opening' : 'closing';
      animation = panel.animate([
        {opacity, transform},
        {opacity: value ? 1 : 0, transform: value ? 'translateY(0)' : 'translateY(-10px)'}
      ], {duration: 300, easing: motion.easing, fill: 'both'});
      animation.onfinish = settle;
    }
    menus.set(menu, {setOpen, settle});
    summary.setAttribute('aria-expanded', String(desired));
    summary.addEventListener('click', event => { event.preventDefault(); setOpen(!desired); });
  }
  function updateHeader() {
    scrollFrame = 0;
    document.querySelectorAll('.auluo-site-header').forEach(header => {
      const scrolled = window.scrollY > 12;
      header.classList.toggle('auluo-header-scrolled', scrolled);
      header.closest('.auluo-section')?.classList.toggle('auluo-header-scrolled', scrolled);
    });
  }
  function openDisclosure(element, animate = true) {
    const controller = disclosures.get(element);
    if (controller) controller.setOpen(true, animate);
    else element.open = true;
  }

  function enhanceDisclosure(details, group = null) {
    if (disclosures.has(details)) return;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    let desired = details.open;
    let physical = details.open;
    let animation = null;
    let previousOverflow = details.style.overflow;
    let focusWhenFinished = false;

    function writeOpen(value) {
      physical = value;
      details.open = value;
    }
    function focusEmail() {
      if (focusWhenFinished && desired && summary.contains(document.activeElement) && !details.querySelector('[data-form-status]')) {
        details.querySelector('input[type="email"]')?.focus({preventScroll: true});
      }
      focusWhenFinished = false;
    }
    function settle() {
      if (animation) {
        animation.onfinish = null;
        animation.cancel();
        animation = null;
      }
      writeOpen(desired);
      details.style.overflow = previousOverflow;
      details.removeAttribute('data-auluo-state');
      active.delete(controller);
      focusEmail();
    }
    function setOpen(value, animate = true, userInitiated = false) {
      if (desired === value && details.open === value && !animation) return;
      if (desired === value && animation) return;
      const startHeight = details.getBoundingClientRect().height;
      const wasAnimating = Boolean(animation);
      if (animation) {
        animation.onfinish = null;
        animation.cancel();
        animation = null;
      }
      if (!wasAnimating) previousOverflow = details.style.overflow;
      desired = value;
      focusWhenFinished = value && userInitiated && details.matches('.auluo-signup');
      if (value && group) {
        group.querySelectorAll('.auluo-ingredient').forEach(other => {
          if (other !== details) disclosures.get(other)?.setOpen(false, animate);
        });
      }
      if (!value && details.contains(document.activeElement) && !summary.contains(document.activeElement)) {
        summary.focus({preventScroll: true});
      }
      if (!animate || reduced.matches || typeof details.animate !== 'function') {
        settle();
        return;
      }
      // Keep the native content available while height decreases; close only at the end.
      writeOpen(true);
      const style = getComputedStyle(details);
      const n = value => parseFloat(value) || 0;
      const edges = n(style.borderTopWidth) + n(style.borderBottomWidth) + n(style.paddingTop) + n(style.paddingBottom);
      let endHeight = details.getBoundingClientRect().height;
      if (!value) {
        // Measure the actual closed state, including summary styles that depend on [open].
        writeOpen(false);
        endHeight = details.getBoundingClientRect().height;
        writeOpen(true);
      }
      const adjustment = style.boxSizing === 'border-box' ? 0 : edges;
      if (Math.abs(endHeight - startHeight) < 1) {
        settle();
        return;
      }
      details.style.overflow = 'hidden';
      details.dataset.auluoState = value ? 'opening' : 'closing';
      active.add(controller);
      try {
        animation = details.animate([
          {height: `${Math.max(0, startHeight - adjustment)}px`},
          {height: `${Math.max(0, endHeight - adjustment)}px`}
        ], details.closest('.a47-policy-sections') ? {...motion, duration: 420} : motion);
        animation.onfinish = settle;
      } catch (_) {
        settle(); // A browser without animation support retains native disclosure behavior.
      }
    }
    const controller = {setOpen, settle, element: details};
    disclosures.set(details, controller);
    summary.addEventListener('click', event => {
      // Native summary generates clicks for Enter/Space, preserving keyboard semantics.
      if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea')) return;
      event.preventDefault();
      setOpen(!desired, true, true);
    });
    details.addEventListener('toggle', () => {
      // Honor external native changes without treating our own open mutations as another click.
      if (details.open === physical) return;
      physical = details.open;
      const target = details.open;
      desired = !target;
      setOpen(target, false);
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-auluo-menu]').forEach(menu => {
      if (instances.has(menu)) return;
      instances.add(menu);
      enhanceMenu(menu);
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); closeMenu(menu, true); }
      });
      menu.addEventListener('click', event => {
        if (event.target.closest('a')) closeMenu(menu);
      });
      menu.addEventListener('focusout', event => {
        if (!event.relatedTarget) return;
        requestAnimationFrame(() => { if (!menu.contains(document.activeElement)) closeMenu(menu); });
      });
    });
    scope.querySelectorAll('[data-auluo-accordion]').forEach(group => {
      const rows = group.querySelectorAll('.auluo-ingredient');
      rows.forEach(row => enhanceDisclosure(row, group));
      // The native name attribute remains in Liquid for no-JS use. Enhanced groups
      // coordinate themselves so a sibling can animate closed instead of snapping.
      rows.forEach(row => { if (disclosures.has(row)) row.removeAttribute('name'); });
    });
    scope.querySelectorAll('.auluo-methodology, .auluo-signup, details.auluo-formula-card, .auluo-faq-item, .a9-label, .a47-policy-sections details').forEach(details => enhanceDisclosure(details));
    scope.querySelectorAll('[data-form-status]').forEach(status => {
      if (instances.has(status)) return;
      instances.add(status);
      const details = status.closest('details');
      if (details) openDisclosure(details, false);
      status.focus({preventScroll: true});
    });
    if (!reduced.matches && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
      if (!observer) observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) { entry.target.classList.add('auluo-revealed'); observer.unobserve(entry.target); }
        });
      }, {threshold: .04, rootMargin: '0px 0px -12px 0px'});
      scope.querySelectorAll('[data-auluo-reveal]').forEach(el => {
        if (instances.has(el)) return;
        instances.add(el);
        if (el.getBoundingClientRect().top < innerHeight) return;
        el.classList.add('auluo-reveal-ready');
        observer.observe(el);
      });
    }
    updateHeader();
  }

  document.addEventListener('click', event => {
    if (event.defaultPrevented) return;
    document.querySelectorAll('[data-auluo-menu][open]').forEach(menu => {
      if (!menu.contains(event.target)) closeMenu(menu);
    });
    const link = event.target.closest('a[href^="#"]');
    if (!link || !link.closest('.auluo-section')) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    if (target.matches('details')) openDisclosure(target);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({preventScroll: true});
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(controller => { if (event.target.contains(controller.element)) controller.settle(); });
    event.target.querySelectorAll('[data-auluo-reveal]').forEach(el => observer?.unobserve(el));
  });
  document.addEventListener('shopify:block:select', event => {
    const row = event.target.closest('.auluo-ingredient, details.auluo-formula-card');
    if (row) openDisclosure(row, false);
  });
  window.addEventListener('scroll', () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateHeader);
  }, {passive: true});
  window.addEventListener('resize', () => {
    // Finish at natural height so changing viewport never leaves content clipped.
    const width = document.documentElement.clientWidth;
    if (width !== lastViewportWidth) active.forEach(controller => controller.settle());
    lastViewportWidth = width;
    if (innerWidth >= 750) document.querySelectorAll('[data-auluo-menu]').forEach(menu => closeMenu(menu));
    updateHeader();
  }, {passive: true});
  let lastViewportWidth = document.documentElement.clientWidth;
  reduced.addEventListener('change', () => {
    active.forEach(controller => controller.settle());
    document.querySelectorAll('[data-auluo-menu]').forEach(menu => menus.get(menu)?.settle());
    if (reduced.matches) revealAll();
  });
  window.addEventListener('beforeprint', () => {
    active.forEach(controller => controller.settle());
    revealAll();
  });
  window.AuluoHome = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
